import type { RefObject } from 'preact';
import { useEffect } from 'preact/hooks';

// Shared sheet behavior for modal picker sheets (ArcPickerSheet, RolePickerSheet):
//   - Lock body scroll while open
//   - Focus the first interactive element on open
//   - Restore focus to the previously-focused element on close
//   - Dismiss on Escape
export function usePickerSheet(
  open: boolean,
  sheetRef: RefObject<HTMLElement>,
  onDismiss: () => void,
): void {
  useEffect(() => {
    if (!open) return;

    const lastFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusable = sheetRef.current?.querySelector<HTMLElement>(
      'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      lastFocused?.focus?.();
    };
  }, [open, sheetRef, onDismiss]);
}
